import { OfferingCard } from '@ole-ui/ole-ui-components';
import useMobxStores from 'hooks/useMobxStores';
import React, { ComponentProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOfferingCardContext } from '../OfferingCardContext';
import offeringTypeRegistry from '../configs';
import { ActionButtonConfig } from '../utils/types';
import { isCatalogEntry } from '../utils/typeGuards';

type FooterBuilderProps = {
  children?: React.ReactNode;
  hideAllActionButtons?: boolean;
  lvcUuid?: string;
  sessionDocId?: string;
  skillsAssessmentId?: string;
} & ComponentProps<typeof OfferingCard.Footer>;

const withSpaNavigation = (
  config: ActionButtonConfig | undefined,
): ActionButtonConfig | undefined => {
  if (!config?.onClick || !config?.href) return config;

  const { onClick: spaNavigate } = config;
  return {
    ...config,
    onClick: (e?: React.MouseEvent) => {
      const isNewTabClick =
        e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0);

      if (!isNewTabClick) {
        e?.preventDefault();
        spaNavigate();
      }
    },
  };
};

const FooterBuilder = ({
  children,
  hideAllActionButtons,
  lvcUuid,
  sessionDocId,
  skillsAssessmentId,
  ...offeringCardFooterProps
}: FooterBuilderProps) => {
  const { t } = useTranslation();
  const { catalogStore, routerStore } = useMobxStores();
  const {
    entry,
    hasEntry,
    hasProgress,
    isLocked,
    isPremiumCard,
    isSearch,
    learningPath,
    liveEventInfo,
    lvcStatus,
    offeringType,
    premiumSessionSlug,
    premiumSessionStatus,
    sessionCollateralData,
  } = useOfferingCardContext();

  const buttonsConfig = useMemo(() => {
    if (hideAllActionButtons) {
      return {};
    }

    const config = offeringTypeRegistry[offeringType];
    if (!config) return {};

    const displayProgress =
      isSearch || isPremiumCard || isLocked ? false : hasProgress;

    const catalogEntry = isCatalogEntry(entry) && entry;

    const entryCode = hasEntry
      ? catalogEntry?.code
      : sessionCollateralData?.offering_slug?.split('-')?.[0];

    const result = config.actionButtons({
      displayProgress,
      entry: catalogEntry,
      hasProgress: isSearch ? false : hasProgress,
      isEnrolledSkillPath: learningPath?.joined,
      isLocked,
      learningPath,
      liveEventInfo,
      lvcInfo: { lvcStatus, lvcUuid },
      premiumSessionCourseOfferingSlug: sessionCollateralData?.offering_slug,
      premiumSessionSlug,
      premiumSessionStatus,
      catalogStore,
      routerStore,
      sessionDocId,
      skillsAssessmentId,
      t,
    });

    const buttonA = withSpaNavigation(result?.actionButtonA);
    const buttonB = withSpaNavigation(result?.actionButtonB);

    return {
      ...(buttonA && {
        actionButtonA: {
          ...buttonA,
          dataAnalyticsId: `offering-card-action-button-${entryCode}`,
        },
      }),
      ...(buttonB && {
        actionButtonB: {
          ...buttonB,
          dataAnalyticsId: `offering-card-second-action-button-${entryCode}`,
        },
      }),
    };
  }, [
    entry,
    hasEntry,
    hasProgress,
    hideAllActionButtons,
    isLocked,
    isPremiumCard,
    isSearch,
    learningPath,
    lvcStatus,
    lvcUuid,
    offeringType,
    premiumSessionSlug,
    premiumSessionStatus,
    catalogStore,
    routerStore,
    sessionCollateralData,
    sessionDocId,
    t,
  ]);

  return (
    <OfferingCard.Footer {...buttonsConfig} {...offeringCardFooterProps}>
      {children}
    </OfferingCard.Footer>
  );
};

export default FooterBuilder;
